import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import VendorMenu from './VendorMenu';

// Styled components based on your Wine Walk CSS but adapted for festival theme
const FestivalMapContainer = styled.div`
  font-family: 'Arial', sans-serif;
  background-color: #0a0a0a;
  min-height: 100vh;
  color: #ffffff;
  background: linear-gradient(rgba(10, 10, 10, 0.95), rgba(10, 10, 10, 0.95)), 
              radial-gradient(circle at 30% 20%, rgba(212, 175, 55, 0.08), transparent 40%),
              radial-gradient(circle at 70% 60%, rgba(184, 134, 11, 0.05), transparent 50%);
  padding: 20px;
  border-radius: 12px;
  max-height: 600px;
  overflow-y: auto;
`;

const FestivalMapInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FestivalMapHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const FestivalMapTitle = styled.h1`
  font-size: 2.2em;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #d4af37, #f5d76e, #926f34);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: none;
`;

const FestivalMapSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  margin: 0;
`;

const FestivalMapControls = styled.div`
  background-color: #1a1a1a;
  border: 1px solid rgba(212, 175, 55, 0.2);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.7), 0 0 20px rgba(224, 184, 65, 0.1);
  margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 15px;
`;

const MapButton = styled.button`
  padding: 12px 20px;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  font-size: 14px;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #d4af37, #f5d76e, #926f34);
    color: #0a0a0a;
    border: none;
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
    }
  ` : `
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(224, 184, 65, 0.3);
    
    &:hover:not(:disabled) {
      background: rgba(212, 175, 55, 0.1);
      border-color: #d4af37;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  margin: 15px 0;
  padding: 10px;
  border-radius: 8px;
  font-weight: bold;
  display: ${props => props.$visible ? 'block' : 'none'};
  
  ${props => props.type === 'success' ? `
    background: rgba(212, 175, 55, 0.1);
    color: #e0b841;
    border: 1px solid rgba(224, 184, 65, 0.2);
  ` : `
    background: rgba(255, 107, 107, 0.1);
    color: #ff6b6b;
    border: 1px solid rgba(255, 107, 107, 0.2);
  `}
`;

const MapContainer = styled.div`
  background: #1a1a1a;
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.7), 0 0 20px rgba(224, 184, 65, 0.1);
  height: 400px;
  position: relative;
  margin-bottom: 20px;
`;

const MapDisplay = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const DemoMapOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: white;
  font-size: 18px;
  text-align: center;
  z-index: 1;
`;

const VendorsList = styled.div`
  background: #1a1a1a;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(212, 175, 55, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.7), 0 0 20px rgba(224, 184, 65, 0.1);
`;

const VendorsTitle = styled.h3`
  color: #D4AF37;
  margin-bottom: 15px;
  font-size: 18px;
`;

const VendorItem = styled.div`
  padding: 15px;
  border: 1px solid #333;
  border-radius: 10px;
  margin-bottom: 10px;
  background: #222;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 3px 10px rgba(212, 175, 55, 0.2);
    transform: translateY(-1px);
    border-color: #D4AF37;
  }
`;

const VendorInfo = styled.div`
  flex: 1;
`;

const VendorName = styled.div`
  font-weight: bold;
  color: #D4AF37;
  margin-bottom: 5px;
  font-size: 16px;
`;

const VendorDetails = styled.div`
  color: #ccc;
  font-size: 14px;
  line-height: 1.4;
`;

const VendorCategory = styled.div`
  font-size: 12px;
  color: #D4AF37;
  margin-top: 2px;
  font-weight: bold;
  text-transform: uppercase;
`;

const VendorLocation = styled.div`
  font-size: 12px;
  color: #888;
  margin-top: 4px;
`;

const VendorActions = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
`;

const ActionButton = styled.button`
  padding: 8px 15px;
  font-size: 12px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #d4af37, #f5d76e, #926f34);
    color: #0a0a0a;
  ` : `
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(224, 184, 65, 0.3);
  `}
  
  &:hover {
    transform: translateY(-1px);
    ${props => props.$primary ? `
      box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
    ` : `
      background: rgba(212, 175, 55, 0.1);
    `}
  }
`;

const UserLocationInfo = styled.div`
  margin-top: 10px;
  padding: 8px;
  background: rgba(212, 175, 55, 0.1);
  border-radius: 6px;
  font-size: 14px;
  color: #e0b841;
`;

const CloseButton = styled.button`
  background-color: #666;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  margin-top: 20px;
  
  &:hover {
    background-color: #777;
  }
`;

const FestivalMap = ({ onClose, onSelectVendor, globalCart, onAddToGlobalCart, onRemoveFromGlobalCart, onUpdateGlobalCartQuantity, getCartItemsForVendor }) => {
  // Configuration - using demo mode for festival map
  const [isTracking, setIsTracking] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [status, setStatus] = useState({ message: '', type: '', visible: false });
  const [watchId, setWatchId] = useState(null);
  const mapRef = useRef(null);
  
  // New state for vendor menu
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showVendorMenu, setShowVendorMenu] = useState(false);

  // Festival vendor data - including Square and Clover integrated vendors
  const festivalVendors = [
    {
      id: 'bbq-smokehouse',
      name: "BBQ Smoke House",
      category: "BBQ Restaurant",
      description: "Authentic Texas-style BBQ with slow-smoked meats and traditional sides",
      specialties: ["Brisket", "Pulled Pork", "Ribs", "Mac & Cheese"],
      location: "Food Court - Section A",
      coordinates: { lat: 28.5493, lng: -81.7731 },
      type: 'restaurant',
      featured: true,
      posSystem: 'square'
    },
    {
      id: 'gourmet-burgers',
      name: "Gourmet Burger Station",
      category: "Burger Restaurant",
      description: "Artisan burgers made with premium ingredients and creative toppings",
      specialties: ["Truffle Burger", "Veggie Deluxe", "BBQ Bacon Burger"],
      location: "Main Street - Booth 12",
      coordinates: { lat: 28.5495, lng: -81.7728 },
      type: 'restaurant',
      featured: false,
      posSystem: null
    },
    {
      id: 'taco-fiesta',
      name: "Taco Fiesta",
      category: "Mexican Restaurant",
      description: "Authentic Mexican street food with fresh ingredients and bold flavors",
      specialties: ["Fish Tacos", "Carnitas", "Vegetarian Tacos", "Elote"],
      location: "Festival Plaza - Corner Spot",
      coordinates: { lat: 28.5491, lng: -81.7735 },
      type: 'restaurant',
      featured: true,
      posSystem: 'square'
    },
    {
      id: 'craft-cocktails',
      name: "Craft Cocktail Corner",
      category: "Bar/Cocktail Lounge",
      description: "Premium cocktails made with top-shelf spirits and fresh ingredients",
      specialties: ["Old Fashioned", "Craft Margarita", "Seasonal Specials"],
      location: "Bar District - Central",
      coordinates: { lat: 28.5494, lng: -81.7732 },
      type: 'bar',
      featured: true,
      posSystem: 'square'
    },
    {
      id: 'pizza-palace',
      name: "Pizza Palace",
      category: "Pizzeria & Italian",
      description: "Artisan wood-fired pizzas with fresh ingredients and premium toppings",
      specialties: ["Margherita", "Pepperoni", "BBQ Chicken", "Garlic Knots"],
      location: "Food Court - Section B",
      coordinates: { lat: 28.5498, lng: -81.7725 },
      type: 'restaurant',
      featured: true,
      posSystem: 'clover'
    },
    {
      id: 'fresh-salad-co',
      name: "Fresh Salad Co.",
      category: "Healthy Food & Bowls",
      description: "Healthy bowls and fresh salads with premium proteins and dressings",
      specialties: ["Buddha Bowl", "Caesar Salad", "Protein Bowl", "Veggie Delight"],
      location: "Health Hub - Booth 6",
      coordinates: { lat: 28.5497, lng: -81.7726 },
      type: 'restaurant',
      featured: false,
      posSystem: 'clover'
    },
    {
      id: 'coffee-corner-cafe',
      name: "Coffee Corner Café",
      category: "Coffee Shop & Bakery",
      description: "Artisan coffee and fresh baked pastries",
      specialties: ["Espresso", "Cappuccino", "Croissants", "Blueberry Muffins"],
      location: "Morning Market - Booth 8",
      coordinates: { lat: 28.5488, lng: -81.7734 },
      type: 'restaurant',
      featured: false,
      posSystem: 'clover'
    },
    {
      id: 'asian-fusion-kitchen',
      name: "Asian Fusion Kitchen",
      category: "Asian Cuisine",
      description: "Authentic Asian cuisine with modern fusion twists",
      specialties: ["Pad Thai", "Sushi Rolls", "Stir Fry", "Dumplings"],
      location: "Festival Plaza - West Side",
      coordinates: { lat: 28.5499, lng: -81.7724 },
      type: 'restaurant',
      featured: true,
      posSystem: 'clover'
    },
    {
      id: 'sandwich-depot',
      name: "Sandwich Depot",
      category: "Deli & Sandwiches",
      description: "Gourmet deli sandwiches with premium meats and fresh bread",
      specialties: ["Italian Sub", "Turkey Club", "Veggie Wrap", "Pastrami"],
      location: "Main Street - Booth 15",
      coordinates: { lat: 28.5500, lng: -81.7723 },
      type: 'restaurant',
      featured: true,
      posSystem: 'clover'
    },
    {
      id: 'local-brewery',
      name: "Local Brew Station",
      category: "Brewery",
      description: "Featuring the best local craft beers and seasonal brews",
      specialties: ["IPA", "Wheat Beer", "Seasonal Ale", "Root Beer Float"],
      location: "Beer Garden - East Side",
      coordinates: { lat: 28.5490, lng: -81.7730 },
      type: 'brewery',
      featured: false,
      posSystem: null
    },
    {
      id: 'artisan-ice-cream',
      name: "Artisan Ice Cream Co.",
      category: "Dessert Shop",
      description: "Small-batch ice cream with unique flavors and premium ingredients",
      specialties: ["Lavender Honey", "Salted Caramel", "Chocolate Bourbon"],
      location: "Sweet Street - Booth 5",
      coordinates: { lat: 28.5492, lng: -81.7729 },
      type: 'restaurant',
      featured: true,
      posSystem: null
    },
    {
      id: 'kettle-corn',
      name: "Kettle Corn Kingdom",
      category: "Snack Stand",
      description: "Fresh popped kettle corn and gourmet popcorn varieties",
      specialties: ["Classic Kettle", "Caramel Corn", "Spicy Cheese"],
      location: "Snack Alley - Booth 2",
      coordinates: { lat: 28.5489, lng: -81.7733 },
      type: 'restaurant',
      featured: false,
      posSystem: null
    }
  ];

  const showStatus = useCallback((message, type) => {
    setStatus({ message, type, visible: true });
    setTimeout(() => {
      setStatus(prev => ({ ...prev, visible: false }));
    }, 5000);
  }, []);

  const getVendorIcon = useCallback((type) => {
    const icons = {
      restaurant: '🍽️',
      bar: '🍸',
      brewery: '🍺',
      winery: '🍷',
      shop: '🛍️'
    };
    return icons[type] || '🍽️';
  }, []);

  const calculateDistance = useCallback((pos1, pos2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (pos2.lat - pos1.lat) * Math.PI / 180;
    const dLon = (pos2.lng - pos1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
             Math.cos(pos1.lat * Math.PI / 180) * Math.cos(pos2.lat * Math.PI / 180) *
             Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  const updatePosition = useCallback((position) => {
    const lat = position.coords.latitude;
    const lng = position.coords.longitude;
    const accuracy = position.coords.accuracy;
    const newUserLocation = { lat, lng };
    setUserLocation(newUserLocation);
    showStatus(`Location updated! Accuracy: ${Math.round(accuracy)}m`, 'success');
  }, [showStatus]);

  const handleLocationError = useCallback((error) => {
    let message;
    switch(error.code) {
      case error.PERMISSION_DENIED:
        message = "Location access denied. Please enable location services.";
        break;
      case error.POSITION_UNAVAILABLE:
        message = "Location information is unavailable.";
        break;
      case error.TIMEOUT:
        message = "Location request timed out.";
        break;
      default:
        message = "An unknown error occurred.";
        break;
    }
    showStatus(message, 'error');
    setIsTracking(false);
  }, [showStatus]);

  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      showStatus('Geolocation is not supported by this browser.', 'error');
      return;
    }
    
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000
    };

    navigator.geolocation.getCurrentPosition(
      updatePosition,
      handleLocationError,
      options
    );
    setIsTracking(true);
    showStatus('Getting your location...', 'success');
  }, [updatePosition, handleLocationError, showStatus]);

  const stopTracking = useCallback(() => {
    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    setIsTracking(false);
    setUserLocation(null);
    showStatus('Location tracking stopped.', 'success');
  }, [watchId, showStatus]);

  const findNearestVendor = useCallback(() => {
    if (!userLocation) {
      showStatus('Please start GPS tracking first.', 'error');
      return;
    }
    
    let nearestVendor = null;
    let nearestDistance = Infinity;
    
    festivalVendors.forEach(vendor => {
      const distance = calculateDistance(userLocation, vendor.coordinates);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestVendor = vendor;
      }
    });
    
    if (nearestVendor) {
      showStatus(
        `Nearest: ${nearestVendor.name} (${nearestDistance.toFixed(1)} km away)`, 
        'success'
      );
    }
  }, [userLocation, calculateDistance, showStatus]);

  const getDirections = useCallback((vendor) => {
    const { lat, lng } = vendor.coordinates;
    const destination = `${lat},${lng}`;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    window.open(url, '_blank');
    showStatus(`Opening directions to ${vendor.name}`, 'success');
  }, [showStatus]);

  const getDistanceToVendor = useCallback((vendor) => {
    if (!userLocation) return null;
    const distance = calculateDistance(userLocation, vendor.coordinates);
    return distance.toFixed(1);
  }, [userLocation, calculateDistance]);

  const handleSelectVendor = useCallback((vendor) => {
    // Check if this vendor has POS integration
    if (vendor.posSystem === 'square') {
      // Show Square menu for this vendor
      setSelectedVendor(vendor);
      setShowVendorMenu(true);
    } else if (vendor.posSystem === 'clover') {
      // Show Clover menu for this vendor
      setSelectedVendor(vendor);
      setShowVendorMenu(true);
    } else {
      // Fallback to original behavior
      if (onSelectVendor) {
        onSelectVendor({
          ...vendor,
          action: 'show_vendor_menu'
        });
      }
    }
  }, [onSelectVendor]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchId]);

  // Handle vendor menu close
  const handleVendorMenuClose = () => {
    setShowVendorMenu(false);
    setSelectedVendor(null);
  };

  // Handle order completion
  const handleOrderComplete = (orderResult) => {
    console.log('Order completed:', orderResult);
    // You can add additional logic here, like showing a success message
    setShowVendorMenu(false);
    setSelectedVendor(null);
  };

  // If showing vendor menu, render it instead of the map
  if (showVendorMenu && selectedVendor) {
    return (
      <VendorMenu
        vendor={selectedVendor}
        onClose={handleVendorMenuClose}
        onOrderComplete={handleOrderComplete}
        globalCart={globalCart}
        onAddToGlobalCart={onAddToGlobalCart}
        onRemoveFromGlobalCart={onRemoveFromGlobalCart}
        onUpdateGlobalCartQuantity={onUpdateGlobalCartQuantity}
        getCartItemsForVendor={getCartItemsForVendor}
      />
    );
  }

  return (
    <FestivalMapContainer>
      <FestivalMapInner>
        <FestivalMapHeader>
          <FestivalMapTitle>🎪 Festival Map</FestivalMapTitle>
          <FestivalMapSubtitle>
            Discover all {festivalVendors.length} festival vendors and their locations
          </FestivalMapSubtitle>
        </FestivalMapHeader>

        <FestivalMapControls>
          <ButtonGroup>
            <MapButton 
              $primary={!isTracking}
              onClick={isTracking ? stopTracking : startTracking}
            >
              {isTracking ? '⏹️ Stop Tracking' : '📍 Find My Location'}
            </MapButton>
            <MapButton onClick={findNearestVendor} disabled={!userLocation}>
              🎯 Find Nearest Vendor
            </MapButton>
          </ButtonGroup>
          
          <StatusMessage $visible={status.visible} type={status.type}>
            {status.message}
          </StatusMessage>

          {userLocation && (
            <UserLocationInfo>
              📍 Your location: {userLocation.lat.toFixed(4)}, {userLocation.lng.toFixed(4)}
            </UserLocationInfo>
          )}
        </FestivalMapControls>

        <MapContainer>
          <MapDisplay ref={mapRef}>
            <DemoMapOverlay>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>🗺️</div>
              <div>Festival Map View</div>
              <div style={{ fontSize: '14px', marginTop: '10px', opacity: 0.8 }}>
                Interactive vendor locations and navigation
              </div>
              <div style={{ fontSize: '12px', marginTop: '20px', opacity: 0.6, maxWidth: '400px' }}>
                GPS tracking and directions work! Scroll down to see all vendor locations.
              </div>
            </DemoMapOverlay>
          </MapDisplay>
        </MapContainer>

        <VendorsList>
          <VendorsTitle>Festival Vendors ({festivalVendors.length})</VendorsTitle>
          <div>
            {festivalVendors.map((vendor) => (
              <VendorItem key={vendor.id}>
                <VendorInfo>
                  <VendorName>
                    {getVendorIcon(vendor.type)} {vendor.name}
                    {vendor.featured && (
                      <span style={{color: '#d4af37', marginLeft: '8px'}}>⭐</span>
                    )}
                    {vendor.posSystem === 'square' && (
                      <span style={{color: '#00D924', marginLeft: '8px', fontSize: '12px'}}>💳 Square POS</span>
                    )}
                    {vendor.posSystem === 'clover' && (
                      <span style={{color: '#FF6B35', marginLeft: '8px', fontSize: '12px'}}>💳 Clover POS</span>
                    )}
                    {userLocation && (
                      <span style={{
                        color: '#666', 
                        fontSize: '12px', 
                        marginLeft: '8px'
                      }}>
                        ({getDistanceToVendor(vendor)} km)
                      </span>
                    )}
                  </VendorName>
                  
                  <VendorCategory>{vendor.category}</VendorCategory>
                  <VendorDetails>{vendor.description}</VendorDetails>
                  <VendorLocation>📍 {vendor.location}</VendorLocation>
                  
                  {vendor.specialties && (
                    <div style={{
                      fontSize: '12px', 
                      color: '#888', 
                      marginTop: '6px',
                      fontStyle: 'italic'
                    }}>
                      Specialties: {vendor.specialties.slice(0, 3).join(', ')}
                    </div>
                  )}
                </VendorInfo>
                
                <VendorActions>
                  <ActionButton 
                    $primary
                    onClick={() => handleSelectVendor(vendor)}
                  >
                    View Menu
                  </ActionButton>
                  <ActionButton onClick={() => getDirections(vendor)}>
                    Directions
                  </ActionButton>
                </VendorActions>
              </VendorItem>
            ))}
          </div>
        </VendorsList>

        <div style={{ textAlign: 'center' }}>
          <CloseButton onClick={onClose}>
            Close Map
          </CloseButton>
        </div>
      </FestivalMapInner>
    </FestivalMapContainer>
  );
};

export default FestivalMap;